import { useEffect, useRef } from 'react'

import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'

import Button from '@space-lib/components/button'
import Icon from '@space-lib/components/icon'
import type { ModalElement } from '@space-lib/components/modal'
import Modal from '@space-lib/components/modal'
import ModalBody from '@space-lib/components/modalBody'
import ModalFooter from '@space-lib/components/modalFooter'
import ModalHeader from '@space-lib/components/modalHeader'

import '@hotmart/cosmos/dist/styles/utilities/screenreaders.css'

import ProgressBar from '@space-lib/components/progressBar'
import { useMediaQuery } from '@space-lib/hooks'
import useTranslation from '@space-lib/hooks/useTranslation'

import { ScreenSizes } from '@space-lib/styles'
import styled from 'styled-components'

import { SURVEY_STEPS } from './context/deleteCommunityModalContext/constants'
import useDeleteCommunityModal from './hooks/useDeleteCommunityModal/useDeleteCommunityModal'
import { MOTIVATION_VALUES } from './motivationStep/constants'
import type { DeleteCommunityModalProps } from './types'

const Hr = styled.hr`
  height: 0.063rem;
`
const CustomProgressBar = styled(ProgressBar)`
  height: 0.7rem;
`

const DeleteCommunityModal = ({ isOpen, closeModal }: DeleteCommunityModalProps) => {
  const modalRef = useRef<ModalElement>(null)
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.md})`)
  const {
    steps,
    isDisabled,
    currentStep,
    setCurrentStep,
    progressBarValue,
    setProgressBarValue,
    motivationSelectedOption
  } = useDeleteCommunityModal()
  const { t } = useTranslation()

  const handleCloseModal = () => {
    setProgressBarValue(0)

    closeModal()

    setCurrentStep(SURVEY_STEPS.MOTIVATION)
  }

  useEffect(() => {
    if (isMobile) {
      const shadowRootModal = modalRef.current?.shadowRoot
      const modalConcreteElement = shadowRootModal?.querySelector(
        'hot-modal-concrete'
      ) as HTMLElement

      const modalContentElement = modalConcreteElement?.shadowRoot?.querySelector(
        '.hot-modal__content'
      ) as HTMLElement

      if (modalConcreteElement?.style) {
        modalConcreteElement.style.paddingRight = '0'
        modalConcreteElement.style.paddingLeft = '0'
      }

      if (modalContentElement?.style) {
        modalContentElement.style.borderTopLeftRadius = '1rem'
        modalContentElement.style.borderTopRightRadius = '1rem'
      }
    }
  })

  const { handleNextStep, handleBackStep, component: CurrentStepComponent } = steps[currentStep]

  const shouldRenderDeleteCommunityButton =
    (currentStep === SURVEY_STEPS.MOTIVATION &&
      Boolean(motivationSelectedOption) &&
      motivationSelectedOption !== MOTIVATION_VALUES.DID_NOT_HAVE_EXPECTED_SUCCESS &&
      motivationSelectedOption !== MOTIVATION_VALUES.DID_NOT_MEET_INITIAL_EXPECTATION) ||
    currentStep === SURVEY_STEPS.EXPECTED_SUCCESS ||
    currentStep === SURVEY_STEPS.INITIAL_EXPECTATION

  const shouldRenderBackButton = currentStep !== SURVEY_STEPS.MOTIVATION

  return (
    <Modal ref={modalRef} show={isOpen} onClose={handleCloseModal} docked={isMobile}>
      <ModalHeader className='_px-5 _pt-5 _pb-3'>
        <div className='_d-flex _align-items-baseline'>
          <p className='_text-4 _m-0 _mr-4 _font-weight-light _text-gray-700'>
            {t('community_survey.delete_community.title')}
          </p>

          <Button
            variation='tertiary'
            className='_p-0 _d-flex _border-0 _bg-transparent _ml-auto _text-gray-400'
            onClick={handleCloseModal}>
            <span className='_sr-only'>{t('general.close')}</span>
            <Icon icon={faXmark} className='_w-5 _h-5' />
          </Button>
        </div>
      </ModalHeader>
      <ModalBody className='_p-0 _px-5'>
        <>
          <p className='_text-2 _text-gray-500 _m-0'>
            {t('community_survey.delete_community.description')}
          </p>
          <Hr className='_my-5 _bg-gray-200' />
          <CustomProgressBar className='_w-full _mb-6' progressValue={progressBarValue} />
        </>

        <CurrentStepComponent />
      </ModalBody>
      <ModalFooter className='_px-5 _pt-6 _pb-5'>
        <div className='_d-flex _ml-auto'>
          {shouldRenderBackButton && (
            <Button className='_mr-5' variation='tertiary' onClick={handleBackStep}>
              {t('buttons.back')}
            </Button>
          )}

          <Button
            disabled={isDisabled}
            variation={shouldRenderDeleteCommunityButton ? 'danger' : 'primary'}
            onClick={handleNextStep}>
            {shouldRenderDeleteCommunityButton && <Icon icon={faTrash} className='_mr-2' />}
            {t(
              shouldRenderDeleteCommunityButton
                ? 'buttons.delete_community'
                : 'community_survey.buttons.next_step'
            )}

            {!shouldRenderDeleteCommunityButton && <Icon icon={faArrowRight} className='_ml-2' />}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default DeleteCommunityModal
